import { useEffect, useId, useRef, useState } from "react";
import { useLocation } from "react-router";
import { CheckIcon } from "lucide-react";
import { isPlanId, planRegistry } from "@/data/meal-plan";
import { useAuth } from "@/context/auth-context";
import { useFriends } from "@/context/friends-context";
import { useFriendMode } from "@/context/friend-mode-context";
import { PersonAvatar } from "@/components/person-avatar";
import { parseWeekPath, switchPersonPath } from "@/lib/week-paths";
import { getCurrentWeekId, getTodayDayOfWeek } from "@/lib/week-utils";
import { cn } from "@/lib/utils";

interface Person {
  /** null: me. */
  friendId: number | null;
  name: string;
  planName: string | null;
}

/**
 * "Planificar comidas de": the avatar at the far left of the navbar that
 * switches whose meals you're planning. Shown once you have an accepted
 * friend (every accepted friend can plan for the other). Picking someone
 * keeps the week and sub-page (`switchPersonPath`).
 *
 * A menu button: Enter/Space/ArrowDown open it on the current person, arrows
 * and Home/End move, Escape or Tab close it and return focus to the button.
 */
export function PersonSwitcher() {
  const { pathname } = useLocation();
  const { user, plan } = useAuth();
  const { friends } = useFriends();
  const { leaveTo } = useFriendMode();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const menuId = useId();

  const accepted = friends.filter((f) => f.status === "accepted");
  const people: Person[] = [
    { friendId: null, name: user?.username ?? "Yo", planName: plan?.name ?? null },
    ...accepted.map((f) => ({
      friendId: f.friend_id,
      name: f.username,
      planName: isPlanId(f.plan_id) ? planRegistry[f.plan_id].name : null,
    })),
  ];
  const currentFriendId = parseWeekPath(pathname)?.friendId ?? null;
  const currentIndex = Math.max(0, people.findIndex((p) => p.friendId === currentFriendId));
  const current = people[currentIndex]!;
  const inFriendMode = currentFriendId !== null;

  // Close on outside click (same pattern as the hamburger menu).
  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open]);

  // Opening focuses the current person.
  useEffect(() => {
    if (open) itemRefs.current[currentIndex]?.focus();
  }, [open, currentIndex]);

  if (accepted.length === 0 || !user) return null;

  function close(returnFocus: boolean) {
    setOpen(false);
    if (returnFocus) buttonRef.current?.focus();
  }

  function pick(person: Person) {
    close(false);
    if (person.friendId === currentFriendId) return;
    const weekId = getCurrentWeekId();
    leaveTo(switchPersonPath(pathname, person.friendId, { weekId, day: getTodayDayOfWeek(weekId) ?? "mon" }));
  }

  function onMenuKeyDown(e: React.KeyboardEvent) {
    const items = itemRefs.current.filter((el): el is HTMLButtonElement => el !== null);
    const at = items.indexOf(document.activeElement as HTMLButtonElement);
    const focusAt = (i: number) => items[(i + items.length) % items.length]?.focus();
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        focusAt(at + 1);
        break;
      case "ArrowUp":
        e.preventDefault();
        focusAt(at - 1);
        break;
      case "Home":
        e.preventDefault();
        focusAt(0);
        break;
      case "End":
        e.preventDefault();
        focusAt(items.length - 1);
        break;
      case "Escape":
        e.preventDefault();
        close(true);
        break;
      case "Tab":
        close(false);
        break;
    }
  }

  const label = inFriendMode
    ? `Planificando para ${current.name}. Cambiar persona`
    : `Planificando para ${current.name} (tú). Cambiar persona`;

  return (
    <div className="relative -ml-1 mr-1 shrink-0" ref={rootRef}>
      <button
        ref={buttonRef}
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown" && !open) {
            e.preventDefault();
            setOpen(true);
          }
        }}
        className="-my-0.5 flex size-9 items-center justify-center rounded-full outline-none transition-colors hover:bg-muted focus-visible:ring-[3px] focus-visible:ring-ring/50"
      >
        <PersonAvatar name={current.name} tone={inFriendMode ? "current" : "self"} />
      </button>

      {open && (
        <div
          id={menuId}
          role="menu"
          aria-label="Planificar comidas de"
          onKeyDown={onMenuKeyDown}
          className="absolute left-0 top-full z-50 mt-1 w-64 rounded-lg border bg-background py-1 shadow-lg"
        >
          <p className="px-4 pb-1 pt-1.5 text-xs font-medium text-muted-foreground" aria-hidden="true">
            Planificar comidas de
          </p>
          {people.map((person, i) => {
            const selected = i === currentIndex;
            return (
              <button
                key={person.friendId ?? "me"}
                ref={(el) => {
                  itemRefs.current[i] = el;
                }}
                type="button"
                role="menuitemradio"
                aria-checked={selected}
                tabIndex={-1}
                onClick={() => pick(person)}
                className={cn(
                  "flex min-h-11 w-full items-center gap-3 px-4 py-1.5 text-left text-sm outline-none transition-colors hover:bg-muted focus-visible:bg-muted",
                  selected && "font-medium",
                )}
              >
                <PersonAvatar name={person.name} tone={person.friendId === null ? "self" : "friend"} className="size-7 text-xs" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate">
                    {person.friendId === null ? `Yo · ${person.name}` : person.name}
                  </span>
                  {person.planName && (
                    <span className="block truncate text-xs font-normal text-muted-foreground">{person.planName}</span>
                  )}
                </span>
                {selected && <CheckIcon aria-hidden="true" className="size-4 shrink-0 text-primary" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
